import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  Box,
  CircularProgress,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';

interface Column {
  id?: string;
  field?: string;
  label?: string;
  headerName?: string;
  minWidth?: number;
  width?: number;
  flex?: number;
  align?: 'left' | 'right' | 'center';
  format?: (value: any) => any;
  renderCell?: (params: any) => any;
}

interface DataTableProps {
  columns: Column[];
  rows?: any[];
  data?: any[];
  loading?: boolean;
  onView?: (row: any) => void;
  onEdit?: (row: any) => void;
  onDelete?: (id: number) => void;
  page?: number;
  rowsPerPage?: number;
  totalCount?: number;
  onPageChange?: (newPage: number) => void;
  onRowsPerPageChange?: (newRowsPerPage: number) => void;
}

const DataTable: React.FC<DataTableProps> = ({
  columns,
  rows,
  data,
  loading = false,
  onView,
  onEdit,
  onDelete,
  page = 0,
  rowsPerPage = 10,
  totalCount = 0,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const tableData = rows || data || [];
  
  const handleChangePage = (event: unknown, newPage: number) => {
    if (onPageChange) onPageChange(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (onRowsPerPageChange) onRowsPerPageChange(parseInt(event.target.value, 10));
  };

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <TableContainer sx={{ maxHeight: 600 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((column, idx) => (
                <TableCell
                  key={column.id || column.field || idx}
                  align={column.align}
                  style={{ minWidth: column.minWidth || column.width }}
                >
                  {column.label || column.headerName}
                </TableCell>
              ))}
              {(onView || onEdit || onDelete) && (
                <TableCell align="right">Actions</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length + 1} align="center">
                  <Box sx={{ p: 3 }}>
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            ) : tableData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length + 1} align="center">
                  No data available
                </TableCell>
              </TableRow>
            ) : (
              tableData.map((row) => {
                // Handle Strapi v4 format - normalize row data
                const rowId = row.id || row.attributes?.id;
                const normalizedRow = row.attributes ? { ...row.attributes, id: row.id } : row;
                
                return (
                  <TableRow hover key={rowId}>
                    {columns.map((column, idx) => {
                      const columnKey = column.id || column.field || '';
                      // For relations, check both normalizedRow and original row
                      // Also handle Strapi REST API format where relations are nested in data
                      let rawValue = columnKey ? (normalizedRow[columnKey] ?? row[columnKey]) : '';
                      
                      // If there's a renderCell, pass the raw value and let it handle conversion
                      if (column.renderCell) {
                        return (
                          <TableCell key={columnKey || idx} align={column.align}>
                            {column.renderCell({ value: rawValue, row: normalizedRow })}
                          </TableCell>
                        );
                      }
                      
                      // If there's a format function, pass the raw value to let it handle the conversion
                      if (column.format) {
                        return (
                          <TableCell key={columnKey || idx} align={column.align}>
                            {column.format(rawValue)}
                          </TableCell>
                        );
                      }
                      
                      // Otherwise, process the value for display
                      let value = rawValue;
                      
                      // If value is a relation object with data wrapper, extract it
                      if (value && typeof value === 'object' && value.data && !value.id && !value.name) {
                        value = value.data;
                      }
                      
                      // Ensure we don't try to render objects directly
                      // Convert objects to strings or extract primitive values
                      if (value && typeof value === 'object' && value !== null) {
                        // If it's an array, join it
                        if (Array.isArray(value)) {
                          value = value.join(', ');
                        }
                        // If it has attributes (Strapi v4 format), extract from attributes
                        else if (value.attributes) {
                          // For relations, try to get name or id from attributes
                          if (value.attributes.name !== undefined) {
                            value = value.attributes.name;
                          } else if (value.attributes.id !== undefined) {
                            value = String(value.attributes.id);
                          } else {
                            value = '';
                          }
                        }
                        // If it has an id or name, use that
                        else if (value.id !== undefined || value.name !== undefined) {
                          value = value.name || String(value.id) || '';
                        }
                        // If it has a data property (Strapi REST API format)
                        else if (value.data) {
                          if (typeof value.data === 'object' && value.data !== null) {
                            if (value.data.attributes?.name) {
                              value = value.data.attributes.name;
                            } else if (value.data.name) {
                              value = value.data.name;
                            } else if (value.data.id) {
                              value = String(value.data.id);
                            } else {
                              value = '';
                            }
                          } else {
                            value = String(value.data);
                          }
                        }
                        // Otherwise, try to stringify safely
                        else {
                          value = String(value);
                        }
                      }
                      
                      return (
                        <TableCell key={columnKey || idx} align={column.align}>
                          {value !== null && value !== undefined ? String(value) : '-'}
                        </TableCell>
                      );
                    })}
                  {(onView || onEdit || onDelete) && (
                    <TableCell align="right">
                      {onView && (
                        <IconButton size="small" onClick={() => onView(normalizedRow)}>
                          <ViewIcon />
                        </IconButton>
                      )}
                      {onEdit && (
                        <IconButton size="small" onClick={() => onEdit(row)}>
                          <EditIcon />
                        </IconButton>
                      )}
                      {onDelete && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => onDelete(rowId)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {onPageChange && (
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
    </Paper>
  );
};

export default DataTable;

